import _ from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import ChatIconOutline from 'components/lib/icons/chat_icon_outline';
import connect from 'components/lib/connect';
import { iconClassFromSessionType } from 'components/lib/icon_classes';
import InlineContainer from 'components/common/containers/inline_container';
import InsetContainer from 'components/common/containers/inset_container';
import InteractionType from 'models/interaction_type';
import { MessagingSessionTypes, RoutingStatus } from 'models/routing_queue_item';
import NavigateToConversation from 'actions/conversation/navigate_to_conversation';
import OutsideClickHandler from 'components/common/utilities/outside_click_handler';
import StackContainer from 'components/common/containers/stack_container';
import track from 'scripts/presentation/components/lib/track';
import TwitterIcon from 'components/lib/icons/twitter_icon';

class MessageNotificationsMenu extends React.Component {
  constructor(props) {
    super(props);

    this.state = { shouldShowMenu: false };
    _.bindAll(this, ['handleClickIcon', 'handleClickMenuRow', 'renderMenuRow', 'handleClickOutside']);
  }

  render() {
    const hasAssignedSessions = !_.isEmpty(this.props.assignedSessions);
    const hasFollowingSessions = !_.isEmpty(this.props.followingSessions);

    if (!hasAssignedSessions && !hasFollowingSessions) {
      return null;
    }

    const hasUnreadMessage =
      _.some(this.props.assignedSessions, session => {
        return this._shouldShowUnread(session);
      }) ||
      _.some(this.props.followingSessions, session => {
        return this._shouldShowUnread(session);
      });

    return (
      <OutsideClickHandler onClickOutside={this.handleClickOutside}>
        <NotificationsMenu data-aid="messageNotificationsMenu">
          <MenuIconContainer data-aid="messageNotificationsMenu-iconContainer" onClick={this.handleClickIcon}>
            <ChatIconOutline className="messageNotificationsMenu-icon" />
          </MenuIconContainer>
          {this.state.shouldShowMenu ? (
            <MenuContent data-aid="messageNotificationsMenu-content">
              {hasAssignedSessions ? (
                <div data-aid="messageNotificationsMenu-assignedContainer">
                  <MenuSubheader inset="none" stack="small">
                    Assigned
                  </MenuSubheader>
                  {this.props.assignedSessions.map(this.renderMenuRow)}
                </div>
              ) : null}
              {hasAssignedSessions && hasFollowingSessions ? <MenuDivider /> : null}
              {hasFollowingSessions ? (
                <div data-aid="messageNotificationsMenu-followingContainer">
                  <MenuSubheader inset="none" stack="small">
                    Following
                  </MenuSubheader>
                  {this.props.followingSessions.map(this.renderMenuRow)}
                </div>
              ) : null}
            </MenuContent>
          ) : null}
        </NotificationsMenu>
        {hasUnreadMessage ? <UnreadMessageBubble className="unreadMessage-iconBubble" /> : null}
      </OutsideClickHandler>
    );
  }

  renderMenuRow(menuItem) {
    return (
      <MenuItemRow
        data-aid="messageNotificationsMenu-item"
        key={`mnm-${menuItem.id}`}
        onMouseDown={track.click('mnm-customer', this.handleClickMenuRow.bind(this, menuItem))}
        inset="none"
        stack="small"
      >
        <InlineContainer inset="none">
          <IconWrapper>{this.getMenuItemIcon(menuItem)}</IconWrapper>
          <CustomerNameLabel
            className={classnames({ 'messageNotificationsMenu-customerName-bold': this._shouldShowUnread(menuItem) })}
            data-aid="messageNotificationsMenu-customerName"
          >
            {menuItem.text}
          </CustomerNameLabel>
        </InlineContainer>
        {this._shouldShowUnread(menuItem) ? (
          <UnreadMessageBubble data-aid="messageNotificationsMenu-unreadRow-icon" />
        ) : null}
      </MenuItemRow>
    );
  }

  handleClickIcon(evt) {
    this.setState({ shouldShowMenu: !this.state.shouldShowMenu });
  }

  handleClickOutside() {
    this.setState({ shouldShowMenu: false });
  }

  handleClickMenuRow(menuItem) {
    this.props.onSelect(menuItem.value);
    this.setState({ shouldShowMenu: false });
  }

  _shouldShowUnread(menuItem) {
    return menuItem.isUnread && this.props.currentConversationId !== menuItem.value.conversationId;
  }

  getMenuItemIcon(menuItem) {
    switch (menuItem.iconType) {
      case InteractionType.CHAT:
        return <MenuRowChatIcon className="messageNotificationMenu-row-chatIcon" />;
      case InteractionType.TWITTER:
        return <MenuRowTwitterIcon className="messageNotificationMenu-row-twitterIcon" />;
      default:
        return <i className={iconClassFromSessionType(menuItem.iconType)} />;
    }
  }
}

MessageNotificationsMenu.propTypes = {
  assignedSessions: PropTypes.arrayOf(Object).isRequired,
  currentConversationId: PropTypes.string,
  followingSessions: PropTypes.arrayOf(Object).isRequired,
  onSelect: PropTypes.func.isRequired,
};

const MessageNotificationsMenuContainer = connect(mapStateToProps, mapExecuteToProps)(MessageNotificationsMenu);

function mapStateToProps({ getProvider }) {
  const currentLocation = getProvider('currentLocation').get();
  const currentAgent = getProvider('currentAgent').get();
  const currentAgentId = currentAgent && currentAgent.id;
  const activeSessions = getProvider('activeSessions').findAll({
    filter: s =>
      s.queueItem.status === RoutingStatus.ACTIVE &&
      _.some(s.queueItem.sessions, session => MessagingSessionTypes.indexOf(session.type) !== -1),
    sortBy: s =>
      -1 *
      Date.parse(
        _.chain(s.queueItem.agents)
          .find({ id: currentAgentId })
          .get('updatedAt')
          .value()
      ),
  });

  let [assignedSessions, followingSessions] = _.partition(activeSessions, s => isAssignee(s, currentAgentId));

  let assignedMenuItems = _.map(assignedSessions, activeSessionAsMenuItem);
  let followingMenuItems = _.map(followingSessions, activeSessionAsMenuItem);

  return {
    assignedSessions: assignedMenuItems,
    currentConversationId: currentLocation.currentConversationId,
    followingSessions: followingMenuItems,
  };
}

function isAssignee(session, agentId) {
  return _.get(session, 'queueItem.assignee.agentId') === agentId;
}

function activeSessionAsMenuItem(activeSession) {
  let iconType = _(activeSession.queueItem.sessions)
    .filter(session => MessagingSessionTypes.indexOf(session.type) !== -1)
    .last().type;

  return {
    id: activeSession.id,
    iconType,
    text: activeSession.getCustomerDisplayName(),
    isUnread: activeSession.isUnread(),
    value: {
      customerId: activeSession.customer.id,
      conversationId: activeSession.queueItem.conversationId,
    },
  };
}

function mapExecuteToProps(executeAction) {
  return {
    onSelect: value => executeAction(NavigateToConversation, value),
  };
}

export { MessageNotificationsMenu };
export default MessageNotificationsMenuContainer;

const NotificationsMenu = styled.div`
  align-items: center;
  display: flex;
  margin-left: 20px;
`;

const MenuIconContainer = styled.div`
  cursor: pointer;
  margin-top: 4px;
  stroke: ${p => p.theme.colors.gray900};
  width: 23px;
  &.messageNotificationsMenu-icon {
    height: 100%;
  }
`;

const MenuContent = styled(InsetContainer)`
  background-color: ${p => p.theme.colors.white};
  box-shadow: ${p => p.theme.boxShadow.medium};
  margin-left: -30px;
  max-height: 300px;
  min-width: 200px;
  overflow: auto;
  position: absolute;
  top: 60px;
`;

const IconWrapper = styled.div`
  align-self: center;
  background: transparent;
  border: none;
  border-radius: 0;
  line-height: 1;
  position: relative;
`;

const UnreadMessageBubble = styled.div`
  align-self: center;
  background: ${p => p.theme.colors.green400};
  border-radius: 50%;
  height: 8px;
  margin: 0px;
  width: 8px;
  &.unreadMessage-iconBubble {
    bottom: 9px;
    position: relative;
    right: 7px;
  }
`;

const MenuRowChatIcon = styled(ChatIconOutline)`
  height: 13px;
  max-width: 15px;
  stroke: ${p => p.theme.colors.gray900};
`;

const MenuRowTwitterIcon = styled(TwitterIcon)`
  height: 13px;
  max-width: 15px;
  stroke: ${p => p.theme.colors.gray900};
`;

const CustomerNameLabel = styled.div`
  padding-left: ${p => p.theme.spacing.small};
  &.messageNotificationsMenu-customerName-bold {
    font-weight: bold;
  }
`;

const MenuSubheader = styled(StackContainer)`
  color: ${p => p.theme.colors.gray600};
  font-size: ${p => p.theme.fontSize.small};
`;

const MenuItemRow = styled(StackContainer)`
  cursor: pointer;
  flex-direction: row;
  justify-content: space-between;
  &:hover {
    color: ${p => p.theme.colors.green400};
    stroke: ${p => p.theme.colors.green400};
  }
`;

const MenuDivider = styled.div`
  border: 0.5px solid ${p => p.theme.colors.gray600};
  margin: 8px 0px 16px 0px;
  width: 100%;
`;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/page_layout/message_notifications_menu.jsx